/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from 'react';
import { Divider, Stack, useMediaQuery, useTheme } from '@mui/material';

import TestIds from '../../../../testIds';
import { formatPrice, saveScrollYPosition } from '../../../../util/helpers';
import StatusChip from '../../../../components/StatusChip';
import { ItemContainer, ItemLabel, ItemValue } from '../../../../components/styled';
import { Quote } from '../../storage/types';
import Card, { CardAction, CardTitle } from '../../../../components/Card';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollYPositionKeys } from '../../../../util/consts';
import {
  PriceWrapper,
  TopContainer,
  CardTitleWrapper,
  StatusWrapper,
  BottomContainer,
} from '../../../orders/components/OrderCardComponents';
import { mapQuoteStatus } from '../../utils/getQuoteStatus';
import { calculateTotalDiscount, calculateDiscountedPriceIncGst } from '../../utils/calculateTotal';
import { QuoteExpiry } from './QuoteExpiry';
import { QuoteCreatedAt } from './QuoteCreatedAt';

interface QuoteCardProps {
  data: Quote;
}

const QuoteCard = ({ data }: QuoteCardProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const quoteDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    quote: Quote,
  ) => {
    e?.preventDefault();

    const sessionStorageKey = ScrollYPositionKeys.Quotes;
    if (pathname === '/quotes') saveScrollYPosition({ sessionStorageKey });
    else saveScrollYPosition({ sessionStorageKey, position: 0 });

    navigate(`${path}${quote.id}`);
  };

  const totalPrice = useMemo(() => {
    return calculateDiscountedPriceIncGst(data.products, data?.delivery?.freightChargeExcGst);
  }, [data]);

  const discountIncGst = useMemo(() => {
    return calculateTotalDiscount(data.products);
  }, [data]);

  const renderQuoteDetails = React.useMemo(() => {
    const variant = isSmall ? 'body2' : 'h4';
    const totalCost = (
      <ItemContainer wrap={isSmall}>
        <ItemLabel variant='body2'>Total cost:</ItemLabel>
        <ItemValue
          variant={variant}
          component='p'
          color={isSmall ? 'grey700' : 'grey800'}
          fontWeight={isSmall ? 400 : 700}
          data-testid={TestIds.feat.quotes.card.totalCost}
        >
          {formatPrice(totalPrice)}
        </ItemValue>
      </ItemContainer>
    );
    const savings = (
      <ItemContainer wrap={isSmall}>
        <ItemLabel variant='body2'>Savings:</ItemLabel>
        <ItemValue
          variant={variant}
          component='p'
          color={isSmall ? 'grey700' : 'secondary700'}
          fontWeight={isSmall ? 400 : 700}
          data-testid={TestIds.feat.quotes.card.savings}
        >
          {formatPrice(discountIncGst)}
        </ItemValue>
      </ItemContainer>
    );
    return (
      <PriceWrapper>
        {totalCost}
        {discountIncGst > 0 && savings}
      </PriceWrapper>
    );
  }, [data, totalPrice, discountIncGst, isSmall]);

  const mappedStatus = React.useMemo(() => {
    const status = mapQuoteStatus(data.status);
    return status;
  }, [data]);

  const productCountLabel = React.useMemo(() => {
    const suffix = data.products.length === 1 ? 'product' : 'products';
    return (
      data.products.length > 0 && (
        <ItemLabel variant='body2' data-testid={TestIds.feat.quotes.card.numberOfItems}>
          {data.products.length} {suffix}
        </ItemLabel>
      )
    );
  }, [data]);

  return (
    <Card
      variant='outlined'
      key={data.id}
      data-id={data.id}
      data-testid={TestIds.feat.quotes.card.card}
    >
      <CardAction
        tabIndex={-1}
        disableRipple
        onClick={(e) => quoteDetailsNavigate(e, '/quotes/', data)}
      >
        <TopContainer>
          <CardTitleWrapper>
            <CardTitle
              title={`Quote #${data.reference}`}
              testId={TestIds.feat.quotes.card.title}
              data-id={data.id}
            />
            <StatusWrapper>
              {mappedStatus?.display && (
                <StatusChip
                  label={mappedStatus.display}
                  variant='filled'
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.feat.quotes.card.status}
                />
              )}
              {productCountLabel}
            </StatusWrapper>
          </CardTitleWrapper>
          <QuoteCreatedAt createdAt={data.createdAt} />
          <Divider />
        </TopContainer>
        <Stack spacing={2}>
          <BottomContainer>{renderQuoteDetails}</BottomContainer>
          <QuoteExpiry quote={data} />
        </Stack>
      </CardAction>
    </Card>
  );
};

export default QuoteCard;
